// src/pages/CRNAProfilePage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Button, Container, Spinner, Alert } from 'react-bootstrap';
import { AnimatePresence, motion } from 'framer-motion';
import { Edit, Eye, Lock, Unlock, Bell, ArrowLeft } from 'lucide-react';
import CRNAProfileCard from '../components/Profile/CRNAProfileCard';
import FacilityListingsSwipe from '../components/Profile/FacilityListingsSwipe';
import EditableProfileModal from '../components/Profile/EditableProfileModal';
import MatchesDashboard from '../components/Matches/MatchesDashboard';
import matchingService from '../services/matchingService';
import { AuthContext } from '../contexts/AuthContext';
import './CRNAProfilePage.css';

const panelVariants = {
  enter: direction => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0
  }),
  center: { x: 0, opacity: 1 },
  exit: direction => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0
  })
};

const CRNAProfilePage = () => {
  const { auth } = useContext(AuthContext);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [direction, setDirection] = useState(1);
  const [activeView, setActiveView] = useState('profile'); // 'profile', 'swipe', or 'matches'
  
  // Matches state
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [matchesError, setMatchesError] = useState(null);
  const [hasUnreadMatches, setHasUnreadMatches] = useState(false);

  // locked whenever swipe deck is active
  const [isLocked, setIsLocked] = useState(false);

  // whenever preview <-> swipe toggles, enforce lock state
  // useEffect(() => {
  //   setIsLocked(isSwipeActive);
  // }, [isSwipeActive]);

  // Fetch matches on component mount
  useEffect(() => {
    if (auth?.token) {
      fetchMatches();
    }
  }, [auth]);

  // Fetch provider matches
  const fetchMatches = async () => {
    setLoadingMatches(true);
    setMatchesError(null);
    
    try {
      const response = await matchingService.getProviderMatches();
      console.log('Matches response:', response);
      
      // Handle both possible response formats
      const matchesData = Array.isArray(response) ? response : (response.data || []);
      setMatches(matchesData);
      
      // Check for unread or pending matches
      const unreadMatches = matchesData.filter(
        match => match.status === 'pending' || !match.viewedByProvider
      );
      setHasUnreadMatches(unreadMatches.length > 0);
    } catch (error) {
      console.error('Error fetching matches:', error);
      setMatchesError('Failed to load matches');
    } finally {
      setLoadingMatches(false);
    }
  };

  // apply/remove body scroll lock
  useEffect(() => {
    document.body.style.overflow = isLocked ? 'hidden' : '';
    
    // Add/remove class to body for navbar visibility
    if (isLocked) {
      document.body.classList.add('navbar-hidden');
    } else {
      document.body.classList.remove('navbar-hidden');
    }
  }, [isLocked]);

  // No longer needed since we're using the activeView state
  // const togglePreview = () => {
  //   setDirection(showProfilePreview ? 1 : -1);
  //   setShowProfilePreview(p => !p);
  // };

  const handleModalClose = () => {
    setShowProfileModal(false);
    setProfileRefreshKey(k => k + 1);
  };

  // unlock scroll
  const handleUnlock = () => {
    setIsLocked(false);
  };

  // re-lock on any swipe
  const handleAnySwipe = () => {
    setIsLocked(true);
  };

  // Lock and center the panel
  const handleLock = () => {
    // First find the panel element
    const panelElement = document.querySelector('.panel');
    if (panelElement) {
      // Calculate where to scroll to center the panel
      const rect = panelElement.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const centerY = rect.top + scrollTop + (rect.height / 2) - (window.innerHeight / 2);
      
      // Smooth scroll to center the panel
      window.scrollTo({
        top: centerY,
        behavior: 'smooth'
      });
      
      // Short timeout to allow the scroll to complete before locking
      setTimeout(() => {
        setIsLocked(true);
      }, 300);
    } else {
      // If no panel found, just lock
      setIsLocked(true);
    }
  };

  // View handlers
  const showMatches = () => {
    setActiveView('matches');
    setIsLocked(false);
  };

  const showProfile = () => {
    setActiveView('profile');
    setDirection(-1);
  };

  const showSwipe = () => {
    setActiveView('swipe');
    setDirection(1);
  };

  return (
    <div className="crna-profile-fullscreen">
      <div className="main-content">
        <div className="action-buttons">
          {activeView !== 'matches' ? (
            <>
              <Button 
                variant="light" 
                onClick={showMatches} 
                className="icon-btn match-btn"
              >
                <img src="/matches.png" alt="Matches" width="40" height="40" className="custom-icon" />
                {hasUnreadMatches && <span className="notification-badge"></span>}
              </Button>
              <Button 
                variant={activeView === 'profile' ? "primary" : "light"} 
                onClick={showProfile} 
                className="icon-btn"
              >
                <Eye size={28} />
              </Button>
              <Button 
                variant={activeView === 'swipe' ? "primary" : "light"} 
                onClick={showSwipe} 
                className="icon-btn"
              >
                <img src="/swipe.png" alt="Swipe" width="40" height="40" className="custom-icon" />
              </Button>
              <Button variant="light" onClick={() => setShowProfileModal(true)} className="icon-btn">
                <Edit size={28} />
              </Button>
            </>
          ) : (
            <Button variant="light" onClick={showProfile} className="icon-btn back-btn">
              <ArrowLeft size={28} />
              <span>Back</span>
            </Button>
          )}
        </div>
        {activeView === 'matches' ? (
          <Container className="matches-container">
            {loadingMatches ? (
              <div className="text-center p-5">
                <Spinner animation="border" />
              </div>
            ) : matchesError ? (
              <Alert variant="danger">{matchesError}</Alert>
            ) : (
              <MatchesDashboard 
                matches={matches} 
                onRefresh={fetchMatches} 
              />
            )}
          </Container>
        ) : (
          <AnimatePresence initial={false} exitBeforeEnter custom={direction}>
            {activeView === 'profile' ? (
              <motion.div
                key="preview"
                className="panel preview-container"
                custom={direction}
                variants={panelVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                <CRNAProfileCard refreshKey={profileRefreshKey} />
              </motion.div>
            ) : (
              <motion.div
                key="swipe"
                className="panel swipe-container"
                custom={direction}
                variants={panelVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                {/* pass down onSwipe callback */}
                <FacilityListingsSwipe onSwipe={handleAnySwipe} />
              </motion.div>
            )}
          </AnimatePresence>
        )}
        {/* top-right lock toggle - only show when not in matches view */}
        {activeView !== 'matches' && (
          <Button
            className="icon-btn lock-toggle-btn"
            onClick={isLocked ? handleUnlock : handleLock}
            variant="light"
            size="sm"
          >
            {isLocked
              ? <Lock size={20} color="#d13212" />
              : <Unlock size={20} color="#2c974b" />}
          </Button>
        )}
      </div>

      <EditableProfileModal show={showProfileModal} handleClose={handleModalClose} />
    </div>
    
  );
};

export default CRNAProfilePage;
